import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState, useEffect } from "react";
import { useToast } from "@/features/toast";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Mail, RefreshCw } from "lucide-react";
import { apiClient } from "@/api/config/api-client";
import { API_ENDPOINTS } from "@/api/config/api-endpoints";
import { AuthStage } from "@/components/auth/auth-stage";
import {
  authFieldClass,
  authFooterClass,
  authFormClass,
  authLabelClass,
  authLinkClass,
  authPrimaryButtonClass,
  authStackClass,
  authTitleClass,
} from "@/components/auth/auth-ui";
import { AuthSupportLink } from "@/components/auth/auth-support-link";
import { AuthIconBadge } from "@/components/auth/auth-icon-badge";
import useAuthStore from "@/store/use-auth-store";
import mixpanel from "mixpanel-browser";

// Register form verilerini temizlemek için
const REGISTER_FORM_KEY = "harmonyai_register_form";

const clearFormData = () => {
  try {
    localStorage.removeItem(REGISTER_FORM_KEY);
  } catch (errorResponse) {
    console.error("Form verilerini temizleme hatası:", errorResponse);
  }
};

// Backend'den dönen verification response tipi
interface VerificationResponse {
  success: boolean;
  message: string;
  data?: {
    accessToken?: string;
  };
}

export default function EmailVerificationPage() {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [code, setCode] = useState("");
  const { success, error } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser, setTokens } = useAuthStore();

  // RegisterPage'den gelen email bilgisi
  const email = location.state?.email || "";

  // Email bilgisi yoksa register sayfasına yönlendir
  useEffect(() => {
    if (!email) {
      navigate("/register");
    }
  }, [email, navigate]);

  // Register form verilerinden password'ü al (otomatik login için)
  const getStoredPassword = (): string | null => {
    try {
      const formData = localStorage.getItem("harmonyai_register_form");
      if (formData) {
        const parsed = JSON.parse(formData);
        return parsed.password || null;
      }
    } catch (errorResponse) {
      console.error("Form verisi okuma hatası:", errorResponse);
    }
    return null;
  };

  // Kod girişini sadece sayılarla sınırla
  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
    setCode(value);
  };

  // Kod doğrulama
  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) {
      error(t("auth.enterValidCode"), {
        title: t("auth.verificationError"),
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiClient.post<VerificationResponse>(
        API_ENDPOINTS.verifyUser,
        {
          email,
          code,
        },
      );

      // ✅ CRITICAL: apiClient (401 hariç) 4xx'leri resolve edebildiği için success kontrolü şart.
      if (!response?.success) {
        error(response?.message || t("error.somethingWentWrong"), {
          title: t("auth.verificationError"),
        });
        return;
      }

      // Verification başarılı! Form verilerini temizle
      clearFormData();

      window.netmera.push(function (api) {
        api.sendEvent({
          code: "rxrlv",
        });
      });
      mixpanel.track("email_verified", {
        type: "email_verified",
        email: email,
      });
      // Backend'den accessToken dönerse direkt auth et
      if (response.data?.accessToken) {
        const token = response.data.accessToken;
        setTokens({ accessToken: token, refreshToken: null });

        // Token'ın set edilmesi için kısa bir bekleme
        await new Promise((resolve) => setTimeout(resolve, 100));

        try {
          // User bilgilerini al
          const userResponse = await apiClient.get<{
            success: boolean;
            message: string;
            data: any;
          }>(API_ENDPOINTS.getMe);

          if (userResponse.data) {
            setUser(userResponse.data);

            success(
              t("auth.welcomeToApp", {
                name: userResponse.data.name,
              }),
              {
                title: t("auth.verificationSuccess"),
              },
            );

            // Onboarding sayfasına yönlendir
            navigate("/");
            return; // Bu fonksiyondan çık
          } else {
            throw new Error("Failed to get user profile");
          }
        } catch (userError) {
          console.error("User bilgilerini alma hatası:", userError);
          // User bilgilerini alamazsak fallback'e git
          setTokens({ accessToken: "", refreshToken: null });

          success(t("auth.emailVerified"), {
            title: t("auth.verificationSuccess"),
            actions: [
              { label: t("auth.goToLogin"), action: () => navigate("/login") },
            ],
          });

          // Fallback: login sayfasına yönlendir
          navigate("/login");
          return; // Bu fonksiyondan çık
        }
      } else {
        // Token dönmezse, kayıtlı şifre ile otomatik login dene
        const storedPassword = getStoredPassword();

        if (storedPassword) {
          try {
            success(t("auth.loggingIn"), {
              title: t("auth.verificationSuccess"),
            });

            // Otomatik login yap
            const loginResponse = await apiClient.post<{
              success: boolean;
              data: { accessToken: string; refreshToken?: string };
            }>(API_ENDPOINTS.login, {
              email,
              password: storedPassword,
            });

            if (loginResponse.data?.accessToken) {
              setTokens({
                accessToken: loginResponse.data.accessToken,
                refreshToken: loginResponse.data.refreshToken || null,
              });

              // User bilgilerini al
              try {
                const userResponse = await apiClient.get<{
                  success: boolean;
                  data: any;
                }>(API_ENDPOINTS.getMe);

                if (userResponse.data?.data) {
                  setUser(userResponse.data.data);
                }
              } catch (userError) {
                console.error("User bilgilerini alma hatası:", userError);
                // User bilgilerini alamazsak token'ı temizle
                setTokens({ accessToken: "", refreshToken: null });
                throw userError;
              }

              success(
                t("auth.welcomeToApp", {
                  name: email,
                }),
                {
                  title: t("auth.loginSuccess"),
                },
              );

              // Onboarding sayfasına yönlendir
              navigate("/");
            } else {
              throw new Error("Login response invalid");
            }
          } catch (loginError: any) {
            console.error("Otomatik login hatası:", loginError);

            success(t("auth.emailVerified"), {
              title: t("auth.verificationSuccess"),
              actions: [
                {
                  label: t("auth.goToLogin"),
                  action: () => navigate("/login"),
                },
              ],
            });

            // Login başarısız olursa manuel login için yönlendir
            navigate("/login");
          }
        } else {
          // Şifre bulunamazsa login sayfasına yönlendir
          success(t("auth.emailVerified"), {
            title: t("auth.verificationSuccess"),
            actions: [
              { label: t("auth.goToLogin"), action: () => navigate("/login") },
            ],
          });

          navigate("/login");
        }
      }
    } catch (errorCatch: any) {
      console.error("Verification ana hatası:", errorCatch);
      error(
        errorCatch.response?.data?.message || t("error.somethingWentWrong"),
        {
          title: t("auth.verificationError"),
          actions: [
            { label: t("auth.tryAgain"), action: () => handleVerifyCode },
          ],
        },
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Yeniden kod gönder
  const handleResendCode = async () => {
    setIsResending(true);
    try {
      await apiClient.post(API_ENDPOINTS.resendVerificationCode, {
        email,
      });

      success(t("auth.checkYourEmail"), {
        title: t("auth.codeResent"),
      });

      setCode(""); // Kodu temizle
    } catch (errorResponse: any) {
      console.error("Resend code hatası:", errorResponse);

      // Backend rate limiting hatası için özel mesaj
      if (
        errorResponse.response?.status === 429 ||
        errorResponse.response?.data?.code === "TOO_MANY_REQUESTS" ||
        errorResponse.response?.data?.message?.includes("rate limi")
      ) {
        // Backend'den gelen message'ı parse et
        const backendMessage = errorResponse.response?.data?.message;

        // Backend message'da wait time varsa onu göster, yoksa varsayılan
        const displayMessage = backendMessage || t("auth.resendTooFrequent");

        error(displayMessage, {
          title: t("auth.resendError"),
        });
      } else {
        // Diğer hatalar için genel mesaj
        const errorMessage =
          errorResponse.response?.data?.message ||
          t("error.somethingWentWrong");

        error(errorMessage, {
          title: t("auth.resendError"),
          actions: [
            { label: t("auth.tryAgain"), action: () => handleResendCode() },
          ],
        });
      }
    } finally {
      setIsResending(false);
    }
  };

  return (
    <AuthStage>
          <div className={authStackClass}>
            {/* Header */}
            <div className="flex flex-col items-center gap-4">
              <AuthIconBadge>
                <Mail className="h-6 w-6 text-text-primary dark:text-text-primary" />
              </AuthIconBadge>
              <div className="text-center space-y-2">
                <h1 className={authTitleClass}>
                  {t("auth.verifyEmail")}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {t("auth.verificationCodeSent", { email })}
                </p>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleVerifyCode} className={authFormClass}>
              <div className="space-y-1">
                <label
                  htmlFor="code"
                  className={authLabelClass}
                >
                  {t("auth.verificationCode")}
                </label>
                <Input
                  id="code"
                  type="text"
                  placeholder="------"
                  className={`${authFieldClass} text-center text-2xl tracking-widest`}
                  value={code}
                  onChange={handleCodeChange}
                  maxLength={6}
                  required
                />
                <p className="text-xs text-muted-foreground text-center">
                  {t("auth.enterSixDigitCode")}
                </p>
              </div>

              <Button
                type="submit"
                variant="darkPrimary"
                className={authPrimaryButtonClass}
                disabled={isLoading || code.length !== 6}
              >
                {isLoading ? t("common.loading") : t("auth.verifyCode")}
              </Button>
            </form>

            {/* Resend Section */}
            <div className="space-y-4">
              <Button
                variant="outline"
                onClick={handleResendCode}
                disabled={isResending}
                className="w-full text-text-secondary"
              >
                <RefreshCw
                  className={`h-4 w-4 mr-2 ${
                    isResending ? "animate-spin" : ""
                  }`}
                />
                {isResending ? t("common.loading") : t("auth.resendCode")}
              </Button>

              <div className="flex justify-center">
                <Link
                  to="/register"
                  className={`${authLinkClass} text-sm`}
                >
                  {t("auth.backToRegister")}
                </Link>
              </div>
            </div>

            {/* Destek çıkışı + Language/Theme Switcher */}
            <div className={authFooterClass}>
              <AuthSupportLink />
            </div>
          </div>
    </AuthStage>
  );
}
